import type { ChartFields, ChartData, KpiTile } from '../types';
import { AppHero } from './templates/appHero';
import { BarChart, ColumnChart, StackedColumnChart } from './templates/bars';
import { DonutChart, PieChart } from './templates/donut';
import { FunnelChart } from './templates/funnel';
import { GaugeChart } from './templates/gauge';
import { Heatmap } from './templates/heatmap';
import { KpiRow } from './templates/kpiRow';
import { Histogram } from './templates/histogram';
import { Leaderboard } from './templates/leaderboard';
import { RadarChart } from './templates/radar';
import { RankedColumns } from './templates/rankedColumns';
import { BubbleChart, ScatterPlot } from './templates/scatter';
import { StatTile } from './templates/stat';
import { Sunburst } from './templates/sunburst';
import { DataTable } from './templates/table';
import { AreaChart, LineChart, SplineChart, StackedAreaChart, StepLineChart, TrendChart } from './templates/trend';
import { Treemap } from './templates/treemap';
import { Waterfall } from './templates/waterfall';
import { WorldMap } from './templates/worldmap';
import { buildFrame, isEmpty } from './frame';

/**
 * The chart library.
 *
 * Every template is a pure function of a query result, so any template can
 * render any of the backend catalogue's charts — that is what lets an author
 * pick a form from a gallery instead of hand-wiring each of the ~60 queries.
 *
 * `category` groups the forms in the picker along the lines of the Datylon
 * taxonomy (comparison / temporal / part-to-whole / correlation / distribution
 * / geospatial), so an author browses by the question they're answering.
 */

export type TemplateCategory =
  | 'summary'
  | 'comparison'
  | 'temporal'
  | 'part-to-whole'
  | 'correlation'
  | 'distribution'
  | 'geospatial'
  | 'detail';

/**
 * What every form is handed.
 *
 * `data` and `fields` are all most forms need. `tiles` and `datasets` exist for
 * the composed forms — a KPI row reads one source frame per tile rather than
 * the single merged result, which is what lets one dead source cost one tile
 * instead of the whole row. A form ignores what it does not use.
 */
export interface TemplateProps {
  data: ChartData;
  fields?: ChartFields;
  tiles?: KpiTile[];
  datasets?: Record<string, ChartData>;
}

export interface Template {
  id: string;
  name: string;
  /** What it draws. */
  description: string;
  /** The reader's question this form answers — the thing to pick on. */
  suits: string;
  /** Which picker group it belongs to. */
  category: TemplateCategory;
  /** The form reads per-tile configuration, so the studio offers the tile editor. */
  usesTiles?: boolean;
  Component: (props: TemplateProps) => JSX.Element;
}

/** Human labels for the picker section headings. */
export const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  summary: 'Summary',
  comparison: 'Comparison',
  temporal: 'Data over time',
  'part-to-whole': 'Part-to-whole',
  correlation: 'Correlation',
  distribution: 'Distribution',
  geospatial: 'Geospatial',
  detail: 'Detail',
};

export const TEMPLATES: Template[] = [
  // ---- Summary ------------------------------------------------------------
  // First in the list because this is the thing authors kept hand-writing.
  {
    id: 'kpi-row',
    name: 'KPI row',
    description: 'A row of small tiles — figure, change on the previous period, icon.',
    suits: 'The handful of numbers that lead a dashboard',
    category: 'summary',
    usesTiles: true,
    Component: KpiRow,
  },
  {
    id: 'app-hero',
    name: 'App header',
    description: 'Icon, name, publisher and context chips, read from the first row.',
    suits: 'Naming what the dashboard below is about',
    category: 'summary',
    Component: AppHero,
  },
  // ---- Comparison ---------------------------------------------------------
  {
    id: 'bar',
    name: 'Bar chart',
    description: 'Horizontal bars in query order, values at the tip.',
    suits: 'Comparing a measure across named things',
    category: 'comparison',
    Component: BarChart,
  },
  {
    id: 'column',
    name: 'Column chart',
    description: 'Vertical columns across an ordered axis.',
    suits: 'Comparing periods or ordered buckets',
    category: 'comparison',
    Component: ColumnChart,
  },
  {
    id: 'grouped',
    name: 'Grouped bar chart',
    description: 'Columns clustered side by side, one per sub-category.',
    suits: 'Comparing sub-categories within groups',
    category: 'comparison',
    Component: ColumnChart,
  },
  {
    id: 'leaderboard',
    name: 'Leaderboard',
    description: 'Ranked rows with a magnitude track behind each name.',
    suits: 'Top-N rankings',
    category: 'comparison',
    Component: Leaderboard,
  },
  {
    id: 'ranked-columns',
    name: 'Ranked columns',
    description: 'Several ranked lists side by side, split by one dimension.',
    suits: 'Top-N compared across segments',
    category: 'comparison',
    Component: RankedColumns,
  },
  {
    id: 'stat',
    name: 'Stat tile',
    description: 'One figure, its change on the previous period, a sparkline.',
    suits: 'A single number that matters',
    category: 'comparison',
    Component: StatTile,
  },
  {
    id: 'gauge',
    name: 'Gauge',
    description: 'One value on an arc against an inferred range.',
    suits: 'A single value against a target or ceiling',
    category: 'comparison',
    Component: GaugeChart,
  },
  {
    id: 'radar',
    name: 'Radar chart',
    description: 'One polygon per series over a few shared axes.',
    suits: 'Comparing profiles across a handful of measures',
    category: 'comparison',
    Component: RadarChart,
  },
  {
    id: 'waterfall',
    name: 'Waterfall',
    description: 'Floating bars showing gains and losses to a running total.',
    suits: 'How a figure is built up or drawn down',
    category: 'comparison',
    Component: Waterfall,
  },
  // ---- Data over time -----------------------------------------------------
  {
    id: 'trend',
    name: 'Trend',
    description: 'Lines with a crosshair readout; one series gets a wash.',
    suits: 'A measure over time',
    category: 'temporal',
    Component: TrendChart,
  },
  {
    id: 'line',
    name: 'Line chart',
    description: 'Bare lines through the points, no fill.',
    suits: 'Several measures over time, compared',
    category: 'temporal',
    Component: LineChart,
  },
  {
    id: 'spline',
    name: 'Spline chart',
    description: 'A smoothed line — rounded curves through the points.',
    suits: 'A gentle trend over time',
    category: 'temporal',
    Component: SplineChart,
  },
  {
    id: 'step-line',
    name: 'Step line',
    description: 'The value holds flat, then jumps at each point.',
    suits: 'A measure that changes in discrete steps',
    category: 'temporal',
    Component: StepLineChart,
  },
  {
    id: 'area',
    name: 'Area chart',
    description: 'Lines with the area beneath filled to a wash.',
    suits: 'A cumulative measure over time',
    category: 'temporal',
    Component: AreaChart,
  },
  {
    id: 'stacked-area',
    name: 'Stacked area',
    description: 'Series stacked to a running total, each layer filled.',
    suits: 'How a total splits over time',
    category: 'temporal',
    Component: StackedAreaChart,
  },
  // ---- Part-to-whole ------------------------------------------------------
  {
    id: 'stacked',
    name: 'Stacked columns',
    description: 'Parts of each period, stacked to its total.',
    suits: 'How a total splits, period by period',
    category: 'part-to-whole',
    Component: StackedColumnChart,
  },
  {
    id: 'pie',
    name: 'Pie chart',
    description: 'A full circle split into proportional slices.',
    suits: 'Composition, at a glance only',
    category: 'part-to-whole',
    Component: PieChart,
  },
  {
    id: 'donut',
    name: 'Donut',
    description: 'A pie with an open centre for the total.',
    suits: 'Composition with the total called out',
    category: 'part-to-whole',
    Component: DonutChart,
  },
  {
    id: 'treemap',
    name: 'Treemap',
    description: 'Nested rectangles sized by value; one level of grouping.',
    suits: 'Composition across many segments',
    category: 'part-to-whole',
    Component: Treemap,
  },
  {
    id: 'sunburst',
    name: 'Sunburst',
    description: 'Concentric rings — group on the inside, members on the outside.',
    suits: 'Hierarchical composition',
    category: 'part-to-whole',
    Component: Sunburst,
  },
  {
    id: 'funnel',
    name: 'Funnel',
    description: 'Stages narrowing to an outcome, each band its share.',
    suits: 'A process with drop-off between stages',
    category: 'part-to-whole',
    Component: FunnelChart,
  },
  // ---- Correlation --------------------------------------------------------
  {
    id: 'scatter',
    name: 'Scatter plot',
    description: 'Points on two measure axes.',
    suits: 'Whether two measures are related',
    category: 'correlation',
    Component: ScatterPlot,
  },
  {
    id: 'bubble',
    name: 'Bubble chart',
    description: 'A scatter whose point area carries a third measure.',
    suits: 'Three measures at once',
    category: 'correlation',
    Component: BubbleChart,
  },
  {
    id: 'heatmap',
    name: 'Heatmap',
    description: 'A category × category grid shaded by a measure.',
    suits: 'Where the mass sits across two dimensions',
    category: 'correlation',
    Component: Heatmap,
  },
  // ---- Distribution -------------------------------------------------------
  {
    id: 'histogram',
    name: 'Histogram',
    description: 'A numeric column binned into a frequency distribution.',
    suits: 'How a set of values is spread',
    category: 'distribution',
    Component: Histogram,
  },
  // ---- Geospatial ---------------------------------------------------------
  {
    id: 'worldmap',
    name: 'World map',
    description: 'A world map with the countries in the result highlighted, shaded by measure.',
    suits: 'Where in the world — availability or country-level counts',
    category: 'geospatial',
    Component: WorldMap,
  },
  // ---- Detail -------------------------------------------------------------
  {
    id: 'table',
    name: 'Table',
    description: 'Every column, sortable, exact values.',
    suits: 'Detail and lookup',
    category: 'detail',
    Component: DataTable,
  },
];

/**
 * What an unknown or missing template id renders as.
 *
 * Pinned to the bar chart by id rather than `TEMPLATES[0]`: the list is ordered
 * for the picker, so the first entry changes whenever a form is added at the
 * top — and a chart whose template id no longer exists would then silently
 * change form. A bar chart is the honest default for an unknown id.
 */
const FALLBACK = TEMPLATES.find((t) => t.id === 'bar') ?? TEMPLATES[0];

export const getTemplate = (id: string | undefined): Template =>
  TEMPLATES.find((t) => t.id === id) ?? FALLBACK;

/**
 * The form heuristic, applied to a real result.
 *
 * Picking the form is the first decision and the one that matters most, so the
 * studio makes it for you rather than defaulting everything to a bar chart:
 *
 *   one number            → a stat tile (a one-bar bar chart is not a chart)
 *   dates on the x axis   → a trend
 *   a handful of names    → bars
 *   many names            → a leaderboard, where the ranking is the point
 *   no measure at all     → a table; there is nothing to plot
 */
export const suggestTemplate = (data: ChartData): string => {
  const frame = buildFrame(data);
  if (isEmpty(frame)) return 'table';

  const points = frame.categories.length;
  const seriesCount = frame.series.length;

  if (points === 1 && seriesCount === 1) return 'stat';
  // Time on the x axis is a trend even with several series: comparing them is
  // the usual question. Stacked columns are the deliberate choice for when the
  // question is composition instead, so they stay a manual pick.
  if (frame.temporal) return 'trend';
  if (seriesCount === 1 && points > 12) return 'leaderboard';
  if (points <= 20) return 'bar';
  return 'column';
};
